// API Client for making HTTP requests to the backend
// Supports authentication, token management, and typed responses

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: any;
  requireAuth?: boolean;
  headers?: Record<string, string>;
  /**
   * When false, auth failures for this request do not clear tokens or redirect to /login:
   * - 401 from the API
   * - token refresh failure (expired access token + failed /auth/refresh-token)
   * Use for optional calls (comments list, project members) so they cannot log the user out.
   */
  redirectToLoginOn401?: boolean;
}

export interface ApiResponse<T = any> {
  data: T;
  status: number;
  message?: string;
}

// Token Manager for handling JWT tokens
export class TokenManager {
  private static readonly ACCESS_TOKEN_KEY = 'vishmaker_access_token';
  private static readonly REFRESH_TOKEN_KEY = 'vishmaker_refresh_token';
  private static readonly ID_TOKEN_KEY = 'vishmaker_id_token';
  private static readonly USER_ID_KEY = 'userId';

  static setToken(token: string): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, token);
  }

  static getToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  static setRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  static setIdToken(token: string): void {
    localStorage.setItem(this.ID_TOKEN_KEY, token);
  }

  static getIdToken(): string | null {
    return localStorage.getItem(this.ID_TOKEN_KEY);
  }

  static setUserId(userId: string): void {
    localStorage.setItem(this.USER_ID_KEY, userId);
  }

  static getUserId(): string | null {
    return localStorage.getItem(this.USER_ID_KEY);
  }

  static clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.ID_TOKEN_KEY);
    localStorage.removeItem(this.USER_ID_KEY);
  }

  static isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch {
      return true; // If we can't parse the token, consider it expired
    }
  }
}

import config from '../config/appConfig';

// Base API configuration
const API_BASE_URL = config.apiBaseUrl;

/** In-flight refresh promise so concurrent requests share one refresh */
let refreshPromise: Promise<string | null> | null = null;

/**
 * Refresh access token using refresh token. Updates TokenManager on success.
 * On failure: if redirectOnFailure, clears tokens and sends user to /login; otherwise returns null.
 */
async function refreshAccessToken(redirectOnFailure = true): Promise<string | null> {
  const refreshToken = TokenManager.getRefreshToken();
  if (!refreshToken) return null;
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const refreshResponse = await apiClient<{
        access_token: string;
        refresh_token: string;
        id_token?: string;
        user?: { id?: string };
      }>('/auth/refresh-token', {
        method: 'POST',
        body: { refresh_token: refreshToken },
        requireAuth: false,
      });
      TokenManager.setToken(refreshResponse.access_token);
      TokenManager.setRefreshToken(refreshResponse.refresh_token);
      if (refreshResponse.id_token) {
        TokenManager.setIdToken(refreshResponse.id_token);
      }
      if (refreshResponse.user?.id) {
        TokenManager.setUserId(refreshResponse.user.id);
      }
      return refreshResponse.access_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      if (redirectOnFailure) {
        TokenManager.clearTokens();
        window.location.href = '/login';
      }
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

// Main API client function
async function apiClient<T = any>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const {
    method = 'GET',
    body,
    requireAuth = true,
    headers = {},
    redirectToLoginOn401 = true,
  } = options;

  // Prepare request headers
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers
  };

  // Add authentication header if required; auto-refresh if token expired
  if (requireAuth) {
    let token = TokenManager.getToken();
    if (token && TokenManager.isTokenExpired(token)) {
      token = await refreshAccessToken(redirectToLoginOn401);
    }
    if (token && !TokenManager.isTokenExpired(token)) {
      requestHeaders['Authorization'] = `Bearer ${token}`;
    } else {
      throw new Error('Authentication required but no valid token found');
    }
  }

  // Prepare request options
  const requestOptions: RequestInit = {
    method,
    headers: requestHeaders,
  };

  // Add body for non-GET requests
  if (body && method !== 'GET') {
    requestOptions.body = JSON.stringify(body);
  }

  // Construct full URL
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
  
  // Debug logging
  console.log('🌐 API Call:', {
    endpoint,
    apiBaseUrl: API_BASE_URL,
    fullUrl: url,
    method
  });

  try {
    const response = await fetch(url, requestOptions);

    // Handle non-2xx responses
    if (!response.ok) {
      if (response.status === 401 && requireAuth && redirectToLoginOn401) {
        // Only redirect when we expected to be authenticated (avoid redirecting public calls like waitlist)
        TokenManager.clearTokens();
        window.location.href = '/login';
        throw new Error('Authentication failed');
      }
      if (response.status === 401) {
        const errorData = await response.json().catch(() => ({}));
        const raw = errorData.detail ?? errorData.message ?? 'Unauthorized';
        const message = typeof raw === 'string' ? raw : JSON.stringify(raw);
        throw new Error(message);
      }

      const errorData = await response.json().catch(() => ({}));
      // FastAPI uses "detail" (string or array of { msg, loc }) for validation/HTTP errors
      const raw = errorData.detail ?? errorData.message ?? `HTTP ${response.status}: ${response.statusText}`;
      const message = typeof raw === 'string'
        ? raw
        : Array.isArray(raw) && raw.length > 0 && raw[0].msg
          ? raw[0].msg
          : JSON.stringify(raw);
      throw new Error(message);
    }

    // Handle different response types
    if (response.status === 204) {
      // No Content (common for DELETE operations)
      return {} as T;
    }
    
    // Check if there's content to parse
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      // Parse JSON response
      const data = await response.json();
      return data;
    } else if (response.headers.get('content-length') === '0' || !response.body) {
      // Empty response
      return {} as T;
    } else {
      // Try to parse as JSON, fallback to empty object
      try {
        const data = await response.json();
        return data;
      } catch {
        // If parsing fails, return empty object
        return {} as T;
      }
    }
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}

// Export the main function and TokenManager
export default apiClient;
